
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { App, UserApp } from '@/types/app';
import { Download, Trash2, Settings } from 'lucide-react';

interface AppCardProps {
  app: App;
  userApp?: UserApp & { app: App };
  onInstall: (appId: string) => void;
  onUninstall: (appId: string) => void;
  onToggle: (appId: string, enabled: boolean) => void;
  isInstalling?: boolean;
  isUninstalling?: boolean;
}

export const AppCard = ({ 
  app, 
  userApp, 
  onInstall, 
  onUninstall, 
  onToggle,
  isInstalling,
  isUninstalling 
}: AppCardProps) => {
  const isInstalled = !!userApp;
  const isEnabled = userApp?.is_enabled ?? false;

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'building-standards': return 'bg-blue-100 text-blue-800';
      case 'product-certification': return 'bg-green-100 text-green-800';
      case 'electronics': return 'bg-purple-100 text-purple-800';
      case 'chemicals': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-start gap-3">
          {app.icon_url && (
            <img 
              src={app.icon_url} 
              alt={app.display_name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg leading-tight">{app.display_name}</CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getCategoryColor(app.category)}>
                {app.category.replace('-', ' ')}
              </Badge>
              <span className="text-sm text-muted-foreground">v{app.version}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1">
        <CardDescription className="text-sm leading-relaxed">
          {app.description}
        </CardDescription>
        <p className="text-xs text-muted-foreground mt-2">
          By {app.provider}
        </p>
      </CardContent>
      
      <CardFooter className="flex-shrink-0 pt-4">
        {isInstalled ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Switch
                checked={isEnabled}
                onCheckedChange={(enabled) => onToggle(app.id, enabled)}
                disabled={isUninstalling}
              />
              <span className="text-sm">
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                disabled
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onUninstall(app.id)}
                disabled={isUninstalling}
                className="gap-1"
              >
                <Trash2 className="h-4 w-4" />
                {isUninstalling ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => onInstall(app.id)}
            disabled={isInstalling}
            className="w-full gap-2"
          >
            <Download className="h-4 w-4" />
            {isInstalling ? 'Installing...' : 'Install'}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
